import {defineMessages} from 'react-intl';

import {bbox} from '@turf/turf';
import filter from 'lodash/fp/filter';
import flow from 'lodash/fp/flow';
import isEqual from 'lodash/fp/isEqual';
import keys from 'lodash/fp/keys';
import map from 'lodash/fp/map';
import thru from 'lodash/fp/thru';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import wellknown from 'wellknown';

import {INCLUDE_SUB_ORGS} from '@components/Person/PersonOptionsSelection/form-constants';

import {
  ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID,
  ALERT_ACKNOWLEDGEMENT_STATUSES,
  ALERT_CATEGORIES,
  ALERT_PROVIDERS,
  ALERT_REQUIRE_EXPOSURE,
  ALERT_SEARCH_MODE,
  ALERT_SEARCH_TERMS,
  ALERT_SEVERITIES,
  SEARCH_MODE,
  SITE_REQUIRE_EXPOSURE,
} from '@constants/alert-filters';
import {INCIDENT_IMPACT_LEVEL_TO_SEVERITY} from '@constants/alert-severity-keys';
import {EVENT_PROVIDER} from '@constants/event-providers';
import {FEATURES} from '@constants/features';
import {ALERT_LAYER_ID} from '@constants/map';
import {ALERT_LAYERS} from '@constants/mapbox/mapbox';
import {
  MANAGE_EVENTS,
  VIEW_CAUTION_ALERTS,
  VIEW_CRITICAL_ALERTS,
  VIEW_DATAMINR_ALERTS,
  VIEW_INFORMATIONAL_ALERTS,
  VIEW_INTELLIGENCE_FEED,
  VIEW_WARNING_ALERTS,
} from '@constants/permissions';

import {resolveProvidersForApi} from '@utils/event-providers';
import {formExposureBoundAndDataSource} from '@utils/exposures/utils';
import {featureToggle} from '@utils/featureToggle/featureToggle';

import mapboxUtils, {getBoundsFromFeature} from '../../../../components/Common/Mapbox/utils/mapbox-utils';
import localization from '../../../../services/localization';

export const SEVERITY_MAPPING = [
  {id: 'INFORMATIONAL', permission: VIEW_INFORMATIONAL_ALERTS, severityValue: 'Informational', severityKey: '0'},
  {id: 'CAUTION', permission: VIEW_CAUTION_ALERTS, severityValue: 'Caution', severityKey: '1'},
  {id: 'WARNING', permission: VIEW_WARNING_ALERTS, severityValue: 'Warning', severityKey: '2'},
  {id: 'CRITICAL', permission: VIEW_CRITICAL_ALERTS, severityValue: 'Critical', severityKey: '3'},
];

export const messagesAlertSeverities = defineMessages({
  CRITICAL: {
    id: 'alertSeveritiesUtils.CRITICAL',
    defaultMessage: 'Critical',
  },
  WARNING: {
    id: 'alertSeveritiesUtils.WARNING',
    defaultMessage: 'Warning',
  },
  CAUTION: {
    id: 'alertSeveritiesUtils.CAUTION',
    defaultMessage: 'Caution',
  },
  INFORMATIONAL: {
    id: 'alertSeveritiesUtils.INFORMATIONAL',
    defaultMessage: 'Informational',
  },
  title: {
    id: 'alertSeveritiesUtils.title',
    defaultMessage: 'Alerts',
  },
});

export function generateDetailFeature(alert) {
  function getBounds() {
    const geometry = {
      geometry: geMapCenterPointFromAlert(alert),
    };

    return mapboxUtils.getBoundsFromFeature(geometry);
  }

  return {
    bounds: getBounds(),
    layer: {id: 'alerts-layer'},
    properties: getPropertiesFromAlert(alert),
  };
}

/**
 * Combines a map alert feature with its polygon results
 * to create an alert polygon feature.
 *
 * @param feature
 * @param result
 */
export function parseAlertPolygonResult(feature, result) {
  const shape = get(result, 'data.shape');
  const geometry = wellknown.parse(shape);
  const bounds = mapboxUtils.getBoundingBox(geometry);

  return {
    type: 'Feature',
    properties: feature.properties,
    geometry: geometry,
    bounds: bounds,
  };
}

// HORIZONWEB-11973
// this function replace getMapSelectedPeopleOrgs in the places
// that are not PERSONS related
function getMapSelectedCommonOrgs(state, organizationsFilters) {
  const personOrgsFilter = organizationsFilters?.personOrgs;
  const fullPermissions = get(state, ['session', 'fullPermissions'], []);
  const defaultOrgsFilter = getPermissionKeys(fullPermissions);
  return personOrgsFilter?.length > 0 ? personOrgsFilter : defaultOrgsFilter;
}

export function getPermissionKeys(fullPermissions) {
  return flow([keys])(fullPermissions);
}

/**
 * Returns an object conforming to API for filtering alerts based on current severity and category filters
 * @param state
 * @return {{activeWithinEnd: undefined, categoryIds: *[], activeWithinStart: undefined, organizationIds: string[], locationIds: undefined, includeExpired: undefined, alertSeverities: *[]}}
 */
export function createAlertsFilterFromState(
  state,
  alertsFilters,
  personsFilters,
  datesFilters,
  locationsFilters,
  organizationsFilters,
) {
  const alertSeverities = get(alertsFilters, ALERT_SEVERITIES, []).map(
    (severityValue) => SEVERITY_MAPPING.find((item) => item.severityValue === severityValue).id,
  );
  const categoryIds = get(alertsFilters, ALERT_CATEGORIES, []);
  const locationIds = locationsFilters;
  const activeWithinStart = datesFilters?.beginDateTime;
  const activeWithinEnd = datesFilters?.endDateTime;

  const requirePersonExposures = get(alertsFilters, ALERT_REQUIRE_EXPOSURE, false);

  const requireSiteExposures = get(alertsFilters, SITE_REQUIRE_EXPOSURE, false);

  const organizationIds = getMapSelectedCommonOrgs(state, organizationsFilters);

  const includeSubOrganizations = organizationsFilters?.[INCLUDE_SUB_ORGS];

  const baseFilter = {
    alertSeverities,
    categoryIds,
    locationIds,
    activeWithinStart,
    activeWithinEnd,
    organizationIds,
    includeSubOrganizations,
    requirePersonExposures,
    requireSiteExposures,
  };

  const permissions = get(state, ['session', 'currentPermissions', 'permissions'], {});
  const hasIntelligenceFeedAccess = permissions[VIEW_INTELLIGENCE_FEED];
  const hasManageEvents = permissions[MANAGE_EVENTS];
  const hasDataminrAlerts = permissions[VIEW_DATAMINR_ALERTS];

  if (!featureToggle(FEATURES.HZFE_289) || !hasIntelligenceFeedAccess) {
    return baseFilter;
  }

  const searchTerms = get(alertsFilters, ALERT_SEARCH_TERMS);
  const searchMode = get(alertsFilters, ALERT_SEARCH_MODE, SEARCH_MODE.INCLUDE);
  const acknowledgementStatuses = get(alertsFilters, ALERT_ACKNOWLEDGEMENT_STATUSES);
  const acknowledgementOrganizationId =
    get(alertsFilters, ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID) || get(state, ['session', 'currentPermissions', 'orgId']);
  const providers = get(alertsFilters, ALERT_PROVIDERS);
  const permittedProviders = providers?.filter((provider) => provider !== EVENT_PROVIDER.DATAMINR || hasDataminrAlerts);

  return {
    ...baseFilter,
    ...(!isEmpty(searchTerms) && {
      [ALERT_SEARCH_TERMS]: searchTerms,
      [ALERT_SEARCH_MODE]: searchMode,
    }),
    ...(hasManageEvents &&
      !isEmpty(acknowledgementStatuses) &&
      acknowledgementOrganizationId && {
        acknowledgementStatuses,
        acknowledgementOrganizationId,
      }),
    ...(featureToggle(FEATURES.HZFE_174) && {
      providers: resolveProvidersForApi(permittedProviders, hasDataminrAlerts),
    }),
  };
}

export function geMapCenterPointFromAlert(alert) {
  const center = alert.mapData?.center || {};

  return {
    type: 'Point',
    coordinates: [center.lng, center.lat],
  };
}

function buildSeverityKey(item) {
  return SEVERITY_MAPPING.find((i) => i.id === item.alertSeverity)?.severityKey;
}

export function getPropertiesFromAlert(item) {
  return {
    id: item.alertId,
    fullTitle: item.title,
    severityKey: buildSeverityKey(item),
    modifiedDate: item.modifiedDate,
    publishedDate: item.publishedDate,
    geoJsonUrl: item.mapData?.geoJsonUrl,
    intelType: item?.intelType,
  };
}

export function parseIntelAlertsResults(results) {
  return results?.data?.content?.map((alert) => {
    return {
      type: 'Feature',
      geometry: geMapCenterPointFromAlert(alert),
      properties: getPropertiesFromAlert(alert),
    };
  });
}

export function parseAlertsPolygonFeatureResult(response) {
  response?.data?.content?.sort((a, b) => b?.severity?.key.localeCompare(a?.severity?.key));
  const features = parseIntelAlertsResults(response) || [];
  return features.map((feature) => ({...feature, layer: {id: ALERT_LAYER_ID}}));
}

export function mergeGeoJsonAndAlertDetailsResponses([
  geoJsonUrlResponse,
  alertDetailsResponse,
  exposuresDetailsResponse,
]) {
  const alertDetails = alertDetailsResponse?.value?.data;
  const dataSource = geoJsonUrlResponse?.value?.data;
  const exposureSummary = exposuresDetailsResponse?.value?.data;

  if (dataSource) {
    dataSource.features?.forEach((feature) => {
      feature.bounds = getBoundsFromFeature(feature);
      feature.properties.severity = alertDetails.severity.key;
    });

    alertDetails.parsedMapData = {
      dataSource,
      bounds: getBoundsFromFeature(dataSource),
    };
  } else {
    alertDetails.parsedMapData = formExposureBoundAndDataSource(alertDetails);
  }

  if (exposureSummary) {
    alertDetails.exposures = exposureSummary?.exposures;
  }

  return alertDetails;
}

/**
 * Shared alert-details fetch pipeline used by map detail action flows and local drawer hooks.
 */
export function fetchAlertDetailsForFeature({feature, alertId, locale, intelService, signal}) {
  const geoJsonUrl = get(feature, 'properties.geoJsonUrl');
  const geoJsonPromise = isEmpty(geoJsonUrl) ? null : intelService.getAlertGeoJson(geoJsonUrl, signal);

  const alertDetailsPromise = intelService.getAlertByIdConfigPattern({
    pathParams: {alertId, returnMapImage: false},
    params: {locale},
    ...(signal ? {signal} : {}),
  });

  return Promise.allSettled([geoJsonPromise, alertDetailsPromise]).then(mergeGeoJsonAndAlertDetailsResponses);
}

export function fetchAlertDetailsForFeatureB({alertId, locale, intelService, signal}) {
  const alertDetailsPromise = intelService.getAlertByIdConfigPattern({
    pathParams: {alertId, returnMapImage: false},
    params: {locale},
    ...(signal ? {signal} : {}),
  });

  return alertDetailsPromise.then((alertDetailsResponse) => {
    const geoJsonUrl = get(alertDetailsResponse, 'data.mapData.geoJsonUrl');
    const geoJsonPromise = isEmpty(geoJsonUrl) ? null : intelService.getAlertGeoJson(geoJsonUrl, signal);

    return Promise.allSettled([geoJsonPromise, Promise.resolve(alertDetailsResponse)]).then(
      mergeGeoJsonAndAlertDetailsResponses,
    );
  });
}

/**
 * Returns true if ALERT_LAYERS includes the feature layer.id
 *
 * @param feature
 * @returns {*}
 */
export function isAlertFeature(feature) {
  const layerId = get(feature, 'layer.id');
  return includes(ALERT_LAYERS, layerId);
}

export function isAllAlertLayersTurnedOn(permissionMap, alertFilter) {
  return flow([
    filter((d) => permissionMap[d.permission]),
    map((d) => d.severityValue),
    thru((value) => value.sort()),
    isEqual(alertFilter?.severities?.sort()),
  ])(SEVERITY_MAPPING);
}

export function filterSeveritiesViaPermissions(hasPermission = {}, severities, mapping) {
  return mapping.reduce((accumulator, permissionSeverity) => {
    if (severities?.includes?.(permissionSeverity.severityValue) && hasPermission[permissionSeverity.permission]) {
      accumulator.push(permissionSeverity.severityValue);
    }
    return accumulator;
  }, []);
}

export function getSeverityMappinsValues() {
  return SEVERITY_MAPPING.map((d) => d.severityValue);
}

const messagesIncidentFeatures = defineMessages({
  affectedArea: {
    id: 'messagesIncidentFeatures.affectedArea',
    defaultMessage: 'Affected Area',
  },
  incidentLocation: {
    id: 'messagesIncidentFeatures.incidentLocation',
    defaultMessage: 'Incident Location',
  },
});

export function getIncidentMapParsedData(incidentDetails) {
  const bounds = bbox(incidentDetails?.geometry);
  const features = [];

  if (incidentDetails?.geometry) {
    features.push({
      geometry: incidentDetails?.geometry,
      properties: {
        'feature-description': localization.formatMessage(messagesIncidentFeatures.affectedArea),
        'feature-id': incidentDetails?.id,
        'feature-impact': incidentDetails?.impactLevel,
        'feature-title': incidentDetails?.name,
        severity: INCIDENT_IMPACT_LEVEL_TO_SEVERITY[incidentDetails?.impactLevel],
      },
      bounds,
    });
  }

  if (incidentDetails?.marker) {
    const markerBounds = bbox(incidentDetails?.marker);
    features.push({
      geometry: incidentDetails?.marker,
      properties: {
        'feature-description': localization.formatMessage(messagesIncidentFeatures.incidentLocation),
        'feature-id': incidentDetails?.id,
        'feature-symbol': 'alertdetails_pointofinterest',
      },
      bounds: markerBounds,
    });
  }

  return {
    dataSource: {
      type: 'FeatureCollection',
      features,
    },
    bounds,
  };
}

export function getLayerVisibility(filterType, filter, alertsFilters) {
  const filterAlert = filterType === 'severities' ? filter : alertsFilters?.severities;
  return !!filterAlert?.length;
}
